// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto Genesis tokens; data layer (getInstance query) untouched.
import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { getInstance } from '@/lib/grpcweb/console-api'
import { deploymentTypeLabel, enrollmentStatusLabel, formatRelativeAge } from '@/lib/formatters'
import { GREY_300, GREY_400, GREY_500, WHITE, errorBlockStyle } from '@/lib/genesis'

export function InstanceDetailPage() {
  const { instanceId } = useParams({ from: '/fleet/$instanceId' })
  const { data, isLoading, isError } = useQuery({
    queryKey: ['fleet', 'instance', instanceId],
    queryFn: () => getInstance(instanceId),
  })

  if (isLoading) return <p className="text-sm" style={{ color: GREY_400 }}>Loading…</p>
  if (isError || !data) {
    return (
      <div className="px-3 py-2" style={errorBlockStyle}>
        <p className="text-sm" style={{ color: WHITE }}>Could not load instance {instanceId}.</p>
      </div>
    )
  }

  const inst = data.getInstance()
  if (!inst) {
    return <p className="text-sm" style={{ color: GREY_400 }}>Instance not found.</p>
  }

  return (
    <div>
      <h1 className="font-display mb-1 text-xl font-semibold" style={{ color: WHITE }}>
        {inst.getName() || inst.getId()}
      </h1>
      <p className="mb-6 font-mono text-xs" style={{ color: GREY_500 }}>{inst.getId()}</p>

      <dl className="glass-card mb-8 grid grid-cols-2 gap-x-8 gap-y-3 p-5 text-sm md:grid-cols-3">
        <div>
          <dt style={{ color: GREY_400 }}>Enrollment status</dt>
          <dd style={{ color: GREY_300 }}>{enrollmentStatusLabel(inst.getEnrollmentStatus())}</dd>
        </div>
        <div>
          <dt style={{ color: GREY_400 }}>Deployment type</dt>
          <dd style={{ color: GREY_300 }}>{deploymentTypeLabel(inst.getDeploymentType())}</dd>
        </div>
        <div>
          <dt style={{ color: GREY_400 }}>Last handshake</dt>
          <dd className="font-mono" style={{ color: GREY_300 }}>{formatRelativeAge(inst.getLastHandshakeAt())}</dd>
        </div>
        <div>
          <dt style={{ color: GREY_400 }}>ATF version</dt>
          <dd className="font-mono" style={{ color: GREY_300 }}>{inst.getAtfVersion() || '—'}</dd>
        </div>
        <div>
          <dt style={{ color: GREY_400 }}>Helm chart version</dt>
          <dd className="font-mono" style={{ color: GREY_300 }}>{inst.getHelmChartVersion() || '—'}</dd>
        </div>
        <div>
          <dt style={{ color: GREY_400 }}>License</dt>
          <dd className="font-mono" style={{ color: GREY_300 }}>{inst.getLicenseId() || 'unassigned'}</dd>
        </div>
      </dl>

      <section>
        <h2 className="mb-2 text-sm font-medium" style={{ color: GREY_300 }}>Heartbeat history</h2>
        <div className="rounded-xl px-4 py-6 text-center text-sm" style={{ border: `1px dashed ${GREY_500}`, color: GREY_400 }}>
          Placeholder — no heartbeat history RPC exists yet in console.proto.
          InstanceHeartbeat only updates the instance's current
          last_handshake_at; there is no time-series read path. This section
          is a layout placeholder for a future history view, not populated
          with fabricated data.
        </div>
      </section>
    </div>
  )
}
